import api from '../../../shared/utils/api';
import { getAuthToken } from '../../../shared/utils/api';

const workflowApi = {
  getWorkflows: (projectId) => api.get('/api/workflows', { params: projectId ? { projectId } : {} }),
  getWorkflow: (id) => api.get(`/api/workflows/${id}`),
  createWorkflow: (data) => api.post('/api/workflows', data),

  // The stage catalog, served from the backend's single definition. Used to
  // check the presentation copy in constants.js against the source of truth.
  getStageTypes: () => api.get('/api/workflows/stage-types'),

  // Inverted flow: paste a PRD, get a workflow with contract, collection and
  // running mock provisioned up front. data: { name, prd, projectId? }
  quickStart: (data) => api.post('/api/workflows/quick-start', data),
  deleteWorkflow: (id) => api.delete(`/api/workflows/${id}`),
  updateStage: (workflowId, stageType, data) => api.put(`/api/workflows/${workflowId}/stages/${stageType}`, data),
  addArtifact: (workflowId, stageType, artifact) => api.post(`/api/workflows/${workflowId}/stages/${stageType}/artifacts`, artifact),
  generateArtifact: (workflowId, stageType, prompt) => api.post(`/api/workflows/${workflowId}/stages/${stageType}/generate`, { prompt }),

  // Organization members for assignment dropdown
  getOrgMembers: () => api.get('/api/workflows/org-members'),

  // Artifact versions for a stage
  getArtifacts: (workflowId, stageType, options = {}) => api.get(
    `/api/workflows/${workflowId}/stages/${stageType}/artifacts`,
    { params: options.all ? { all: true } : {} }
  ),

  // Save draft (auto-save)
  saveDraft: (workflowId, stageType, content) => api.post(`/api/workflows/${workflowId}/stages/${stageType}/draft`, { content }),

  // SSE streaming generation (used by "Generate with Agent" button)
  generateStream: (workflowId, stageType, prompt, contextFiles, conversationHistory) => {
    const token = getAuthToken();
    const baseUrl = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE_URL || '');
    const url = `${baseUrl}/api/workflows/${workflowId}/stages/${stageType}/generate-stream`;

    return new Promise((resolve) => {
      let content = '';
      const callbacks = { onToken: null, onDone: null, onError: null };

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
          'Accept': 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ prompt, contextFiles, conversationHistory }),
        credentials: 'include',
      }).then(async (response) => {
        if (!response.ok) {
          const err = await response.text();
          callbacks.onError?.(err || `HTTP ${response.status}`);
          return;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEventType = 'token';

        const processLine = (line) => {
          if (line === '') {
            currentEventType = 'token';
            return;
          }
          if (line.startsWith('event: ') || line.startsWith('event:')) {
            currentEventType = line.replace(/^event:\s*/, '').trim();
            return;
          }
          if (!line.startsWith('data: ') && !line.startsWith('data:')) return;

          const data = line.replace(/^data:\s*/, '').trim();
          if (!data) return;
          let parsed;
          try { parsed = JSON.parse(data); } catch { return; }

          switch (currentEventType) {
            case 'token':
              if (parsed.content !== undefined) {
                content += parsed.content;
                callbacks.onToken?.(parsed.content, content);
              }
              break;
            case 'step_progress':
              callbacks.onStepProgress?.(parsed);
              break;
            case 'insufficient':
              // The intake gate declined to fabricate an artifact and said why.
              callbacks.onInsufficient?.(parsed.message || 'Not enough input to generate.');
              break;
            case 'done':
              callbacks.onDone?.(parsed, content);
              break;
            case 'error':
              callbacks.onError?.(parsed.message || 'Generation failed');
              break;
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          lines.forEach(processLine);
        }
        if (buffer.trim()) processLine(buffer);
      }).catch((err) => {
        callbacks.onError?.(err.message || 'Network error');
      });

      resolve(callbacks);
    });
  },

  // User stories — the spine. Parsed from the PRD, manually editable.
  getUserStories: (workflowId) => api.get(`/api/workflows/${workflowId}/user-stories`),
  parseUserStories: (workflowId, replace = true) => api.post(`/api/workflows/${workflowId}/user-stories/parse`, null, { params: { replace } }),
  createUserStory: (workflowId, data) => api.post(`/api/workflows/${workflowId}/user-stories`, data),
  updateUserStory: (workflowId, storyId, data) => api.put(`/api/workflows/${workflowId}/user-stories/${storyId}`, data),
  deleteUserStory: (workflowId, storyId) => api.delete(`/api/workflows/${workflowId}/user-stories/${storyId}`),
  reorderUserStories: (workflowId, orderedIds) => api.put(`/api/workflows/${workflowId}/user-stories/reorder`, { orderedIds }),

  // Work items — the cells of the story x section grid.
  // Everything assigned to the caller across every workflow — one request, so
  // the dashboard does not have to ask per workflow.
  getMyWorkItems: () => api.get('/api/workflows/my-work-items'),
  getWorkItems: (workflowId, stageType) => api.get(`/api/workflows/${workflowId}/work-items`, { params: stageType ? { stageType } : {} }),
  generateWorkItems: (workflowId, stageType) => api.post(`/api/workflows/${workflowId}/stages/${stageType}/work-items/generate`),
  createWorkItem: (workflowId, stageType, data) => api.post(`/api/workflows/${workflowId}/stages/${stageType}/work-items`, data),
  updateWorkItem: (workflowId, itemId, data) => api.put(`/api/workflows/${workflowId}/work-items/${itemId}`, data),
  deleteWorkItem: (workflowId, itemId) => api.delete(`/api/workflows/${workflowId}/work-items/${itemId}`),

  // Comments on stories and work items (+ @mentions).
  getComments: (workflowId, targetType, targetId) => api.get(`/api/workflows/${workflowId}/comments`, { params: { ...(targetType ? { targetType } : {}), ...(targetId ? { targetId } : {}) } }),
  createComment: (workflowId, data) => api.post(`/api/workflows/${workflowId}/comments`, data),
  deleteComment: (workflowId, commentId) => api.delete(`/api/workflows/${workflowId}/comments/${commentId}`),

  // Capability-based integrations. The user picks a capability (DB engine,
  // Figma, Canva, …) and authenticates; credentials are stored encrypted.
  getIntegrationCapabilities: (workflowId, stageType) => api.get(`/api/workflows/${workflowId}/integration-capabilities`, { params: stageType ? { stageType } : {} }),
  getIntegrations: (workflowId, stageType) => api.get(`/api/workflows/${workflowId}/integrations`, { params: stageType ? { stageType } : {} }),
  connectIntegration: (workflowId, stageType, data) => api.post(`/api/workflows/${workflowId}/stages/${stageType}/integrations`, data),
  resyncIntegration: (workflowId, integrationId) => api.post(`/api/workflows/${workflowId}/integrations/${integrationId}/resync`),
  deleteIntegration: (workflowId, integrationId) => api.delete(`/api/workflows/${workflowId}/integrations/${integrationId}`),

  // Design handoff — screen inventory + linked frames written to the DESIGN artifact.
  generateDesignHandoff: (workflowId) => api.post(`/api/workflows/${workflowId}/design/handoff`),

  // Push token registration (web FCM)
  registerPushToken: (token) => api.post('/api/push/register', { token, platform: 'web' }),

  // Notifications
  getNotifications: (unreadOnly = false) => api.get('/api/workflows/notifications', { params: { unreadOnly } }),
  getNotificationCount: () => api.get('/api/workflows/notifications/count'),
  markNotificationRead: (id) => api.put(`/api/workflows/notifications/${id}/read`),
  markAllNotificationsRead: () => api.put('/api/workflows/notifications/read-all'),
};

export default workflowApi;
